/**
 * @file useBoardFilterOptions - Derive the dynamic slider ranges from board data
 * @description Extracts the dynamic GPR / contract-value bounds from the
 * board's column data. Extracted verbatim from MainContainer (SE-6496) to keep
 * that component under the function-size lint budget.
 *
 * SE-7470 removed the four option LISTS this also returned (positions, teams,
 * countries, contract statuses). They populated the deleted FilterModal's
 * checkbox groups and had no other consumer — `teams` and `countries` had in
 * fact had none since SE-6554, which stopped threading them to the view.
 */

import { useMemo } from "react";

import {
  DEFAULT_CONTRACT_VALUE_RANGE,
  DEFAULT_GPR_RANGE,
} from "../constants/filterDefaults";
import type { ColumnData } from "../types/types";

import { parseNumericValue } from "@/utils/currencyParsing";

/** Dynamic slider ranges derived from board data */
interface BoardFilterOptions {
  dynamicGprRange: [number, number];
  dynamicContractValueRange: [number, number];
}

/**
 * Extract the dynamic slider ranges from board data.
 * @param columnsData - The board's column data (undefined during initial render)
 * @returns Dynamic GPR and contract-value slider bounds
 */
export const useBoardFilterOptions = (
  columnsData: ColumnData[] | undefined
): BoardFilterOptions =>
  useMemo(() => {
    // Guard against undefined columnsData during initial render
    if (!columnsData) {
      return {
        dynamicGprRange: [0, 100] as [number, number],
        dynamicContractValueRange: [0, 100] as [number, number],
      };
    }

    // Flatten all players from columns
    const allPlayers = columnsData.flatMap(column => column.players);

    // Use immutable reduce pattern to accumulate all filter values and ranges
    const accumulated = allPlayers.reduce(
      (acc, player) => {
        const parsedContractValue = parseNumericValue(player.contractValue);

        // Guard against NaN poisoning min/max calculations
        const isGprFinite = Number.isFinite(player.gpr);
        const isContractValueFinite = Number.isFinite(parsedContractValue);

        return {
          minGpr: isGprFinite
            ? Math.min(acc.minGpr, player.gpr as number)
            : acc.minGpr,
          maxGpr: isGprFinite
            ? Math.max(acc.maxGpr, player.gpr as number)
            : acc.maxGpr,
          minContractValue: isContractValueFinite
            ? Math.min(acc.minContractValue, parsedContractValue)
            : acc.minContractValue,
          maxContractValue: isContractValueFinite
            ? Math.max(acc.maxContractValue, parsedContractValue)
            : acc.maxContractValue,
        };
      },
      {
        minGpr: Infinity,
        maxGpr: -Infinity,
        minContractValue: Infinity,
        maxContractValue: -Infinity,
      }
    );

    const { minGpr, maxGpr, minContractValue, maxContractValue } = accumulated;

    // Set sensible defaults if no valid data found (guard against NaN poisoning)
    const rawMinGpr = !Number.isFinite(minGpr) ? 0 : Math.floor(minGpr);
    const rawMaxGpr = !Number.isFinite(maxGpr) ? 100 : Math.ceil(maxGpr);
    const rawMinContractValue = !Number.isFinite(minContractValue)
      ? 0
      : Math.floor(minContractValue);
    const rawMaxContractValue = !Number.isFinite(maxContractValue)
      ? 100
      : Math.ceil(maxContractValue);

    // Ensure min < max to prevent slider crash (Error #185)
    const finalMinGpr =
      rawMinGpr >= rawMaxGpr ? DEFAULT_GPR_RANGE[0] : rawMinGpr;
    const finalMaxGpr =
      rawMinGpr >= rawMaxGpr ? DEFAULT_GPR_RANGE[1] : rawMaxGpr;
    const finalMinContractValue =
      rawMinContractValue >= rawMaxContractValue
        ? DEFAULT_CONTRACT_VALUE_RANGE[0]
        : rawMinContractValue;
    const finalMaxContractValue =
      rawMinContractValue >= rawMaxContractValue
        ? DEFAULT_CONTRACT_VALUE_RANGE[1]
        : rawMaxContractValue;

    return {
      dynamicGprRange: [finalMinGpr, finalMaxGpr] as [number, number],
      dynamicContractValueRange: [
        finalMinContractValue,
        finalMaxContractValue,
      ] as [number, number],
    };
  }, [columnsData]);
